import { useEffect, useRef, useState } from "react";
import { Disc3, X, Send } from "lucide-react";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AndreaChat = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const sid = useRef(localStorage.getItem("uvdj_andrea_sid") || "");

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const openChat = () => {
    setOpen(true);
    if (messages.length === 0) {
      setMessages([{ role: "andrea", text: t("andrea_welcome") }]);
    }
  };

  const send = async (preset) => {
    const msg = (preset ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "andrea", text: "" }]);
    try {
      const resp = await fetch(`${API}/chat/andrea`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message: msg, lang }),
      });
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          let ev;
          try { ev = JSON.parse(p.slice(6)); } catch { continue; }
          if (ev.delta) {
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "andrea", text: c[c.length - 1].text + ev.delta };
              return c;
            });
          }
          if (ev.session_id) {
            sid.current = ev.session_id;
            localStorage.setItem("uvdj_andrea_sid", ev.session_id);
          }
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "andrea", text: t("andrea_error") };
        return c;
      });
    }
    setBusy(false);
  };

  const suggestions = [t("andrea_sug1"), t("andrea_sug2"), t("andrea_sug3")];

  return (
    <>
      <button
        data-testid="andrea-chat-toggle"
        onClick={() => (open ? setOpen(false) : openChat())}
        aria-label={t("andrea_chat_btn")}
        className="fixed bottom-5 right-5 z-50 group flex items-center gap-2.5 rounded-full border border-ember/50 bg-black/80 backdrop-blur-xl pl-3 pr-4 py-2.5 shadow-[0_0_30px_rgba(255,85,0,0.25)] hover:border-gold transition-colors"
      >
        <Disc3 className={`w-5 h-5 text-ember ${busy ? "animate-spin" : "transition-transform duration-700 group-hover:rotate-180"}`} />
        <span className="text-xs font-semibold uppercase tracking-wider text-white">{t("andrea_chat_btn")}</span>
      </button>

      {open && (
        <div
          data-testid="andrea-chat-panel"
          className="fixed z-50 bottom-20 right-5 w-[min(390px,calc(100vw-2.5rem))] h-[min(540px,calc(100dvh-8rem))] rounded-2xl border border-white/15 bg-[#0f0e14]/95 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div>
              <p className="font-display font-bold text-sm tracking-tight">{t("andrea_title")}</p>
              <p className="text-[11px] text-mute">{t("andrea_sub")}</p>
            </div>
            <button data-testid="andrea-chat-close" onClick={() => setOpen(false)} aria-label="chiudi"
              className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-mute hover:text-ember hover:border-ember transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="andrea-chat-messages">
            {messages.map((m, i) => (
              <div key={i} data-testid={`andrea-msg-${i}`}
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-ember text-black rounded-br-sm"
                    : "bg-white/5 border border-white/10 text-white/90 rounded-bl-sm"
                }`}
              >
                {m.text || (busy && i === messages.length - 1 ? "…" : "")}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {suggestions.map((s, i) => (
                <button key={i} data-testid={`andrea-suggestion-${i}`} onClick={() => send(s)}
                  className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1.5 text-[11px] text-gold hover:bg-gold hover:text-black transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            className="px-4 py-3 border-t border-white/10"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            <div className="flex items-center gap-2">
              <input
                data-testid="andrea-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                maxLength={500}
                placeholder={t("andrea_placeholder")}
                className="flex-1 rounded-full border border-white/15 bg-black/40 px-4 py-2.5 text-sm placeholder:text-mute focus:border-ember focus:outline-none"
              />
              <button data-testid="andrea-chat-send" disabled={busy || !input.trim()} aria-label={t("andrea_send")}
                className="w-10 h-10 rounded-full bg-ember flex items-center justify-center text-black hover:bg-gold transition-colors disabled:opacity-40 shrink-0">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default AndreaChat;
